import { z, ZodError } from "zod";
import { Parseable } from "./types";

const maintenanceDataSchema = z.object({
  secondsLeft: z.number(),
  secondsLeftTimestampMs: z.number(),
});

export type MaintenanceData = z.infer<typeof maintenanceDataSchema>;

export const bridgeSchema = z.object({
  show: z.function({ input: [] }),
  close: z.function({ input: [] }),
  claimVoucher: z.function({ input: [z.string()] }),
  claimVoucherQuiet: z.function({ input: [z.string()] }),
  openUrl: z.function({ input: [z.string()] }),
  refreshJwt: z.function({ input: [] }),
  safeArea: z
    .object({
      left: z.number(),
      right: z.number(),
    })
    .optional(),
  getParams: z
    .function({ input: [], output: z.promise(z.string()) })
    .optional()
    .transform((getParams: (() => Promise<string>) | undefined) => {
      return <T>(
        parser: Parseable<T>,
      ): ReturnType<typeof parseBridgeFunctionResult<T>> & {
        bridgeShim?: boolean;
      } => parseBridgeFunctionResult(getParams, parser);
    }),
  markTabAsSeen: z.function({ input: [z.string()] }),
  playSound: z.function({ input: [z.string()] }),
  goBack: z.function({ input: [] }),
  openShop: z.function({ input: [] }),
  openUrlWithSso: z.function({ input: [z.string()] }),
  scidOpenWebSSO: z.function({ input: [z.string()] }).optional(),
  maintenance: maintenanceDataSchema.optional(),
  restart: z.function({ input: [] }).optional(),
  getTimeMs: z.function({ input: [] }).optional(),
});

export const windowSchema = z.object({
  bridge: bridgeSchema,
});

function parseResponseData<T>(
  input: unknown,
  parser: Parseable<T>,
): { output: T; validationError?: ZodError } {
  try {
    return { output: parser.parse(input) };
  } catch (ex) {
    if (ex instanceof ZodError) {
      return { output: input as T, validationError: ex };
    }

    throw ex;
  }
}

export const parseBridgeFunctionResult = async <T>(
  bridgeFn: ((...args: unknown[]) => Promise<string>) | undefined,
  parser: Parseable<T>,
) => {
  const getParamsRes = await bridgeFn?.();

  const { output: gameParams, validationError } = parseResponseData<T>(
    JSON.parse(getParamsRes || '{ "data": {} }').data,
    parser,
  );

  return { gameParams, validationError };
};

export type Bridge = z.infer<typeof bridgeSchema>;

export type BridgeFunctionName = keyof Bridge;
